import React from "react";
import { LoadPhase, PinnedActionBar, SkeletonCard } from "../../../components/ui";

/**
 * The four sections of the form, at the heights they stand at: the workspace
 * name, the brand and its logo, the practice details, and the bank rows.
 *
 * Each is a caption and a card, and each is one block: what is inside a section
 * is the form's own business once it opens, and drawing every field twice is
 * what makes a loading screen busier than the screen it stands for.
 */
const SECTIONS = ["h-[110px]", "h-[218px]", "h-[298px]", "h-[214px]"];

export interface BrandDetailsFormSkeletonProps {
  /**
   * How long the two records have been coming. Only `slow` draws the form's
   * shape; while it is merely `waiting` the column is empty, so details that
   * arrive quickly are never preceded by a screenful of blocks.
   */
  phase: LoadPhase;
}

/**
 * The brand form while the two records behind it are being fetched.
 *
 * The form itself cannot open early — painting empty fields and filling them a
 * moment later reads as the screen overwriting what was typed into it — so this
 * stands in its place, section for section, and the real one takes over with the
 * page already the right shape. The preview beside it waits on the same phase,
 * so the two columns land together rather than one after the other.
 */
const BrandDetailsFormSkeleton: React.FC<BrandDetailsFormSkeletonProps> = ({
  phase,
}) => (
  <div className="flex flex-col lg:h-full lg:min-h-0">
    <div
      role="status"
      className="flex flex-col gap-5 pb-5 lg:min-h-0 lg:flex-1 lg:overflow-hidden"
    >
      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it — and said once, since every block below is hidden from
          screen readers, being the shape of an answer rather than the answer. */}
      <span className="sr-only">Loading your brand details…</span>

      {phase === "slow" &&
        SECTIONS.map((height) => (
          <SkeletonCard key={height} className={height} />
        ))}
    </div>

    {/* The action bar, pinned under the sections the way the form's own is. */}
    {phase === "slow" && (
      <PinnedActionBar className="border-t border-line bg-[#FCFCFD] pt-4">
        <SkeletonCard className="h-[64px]" />
      </PinnedActionBar>
    )}
  </div>
);

export default BrandDetailsFormSkeleton;
